/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Stack, Typography, CircularProgress } from '@mui/material';
import Position from './Position';
import {
  OptionType,
  Position as PositionType,
  StrikeDeltaObject,
} from 'types/optionsCalculator';
import {
  GreeksStatsLookback,
  RawGreeksDataMap,
  RawStatsDataMap,
} from 'types/impliedVol';
import { useCallback } from 'react';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { getVolFromTableData } from 'util/riskEngine';
import { getAdjustedVolData } from 'util/optionsCalculator';
import useToast from 'hooks/useToast';
import { useOptionsCalculatorActions } from 'hooks/optionsCalculator/useOptionsCalculatorActions';

type PositionId = PositionType['id'];

interface SavedPositionsProps {
  greeksLoading?: boolean;
  greeksForSyms?: Map<string, RawGreeksDataMap>;
  greekStatsForSyms?: Map<string, RawStatsDataMap>;
  strikeDataPerExpiration: Map<number, StrikeDeltaObject[]>;
  pricesMap?: Map<string, number>;
  activePositionId: PositionId;
  onSetActivePositionId: (positionId: PositionId) => void;

  // Original saved positions data
  savedPositions: PositionType[];

  // Draft states for saved positions
  savedPositionDrafts: Map<number, PositionType>;
  onUpdateSavedPositionDraft: (
    positionId: PositionId,
    updates: Partial<PositionType>,
  ) => void;

  // Change tracking
  hasUnsavedChanges: Map<number, boolean>;

  // Actions
  onEditSavedPosition: (
    positionId: PositionId,
    position: Partial<PositionType>,
    sym: string,
  ) => Promise<PositionType>;
  onDeleteSavedPosition: (positionId: PositionId) => Promise<void>;

  // Loading state
  isFetchingSavedPositions: boolean;
  setForceDefaultZoom: (forceDefaultZoom: boolean) => void;
}

const SavedPositions = ({
  greeksLoading = false,
  greeksForSyms,
  greekStatsForSyms,
  strikeDataPerExpiration,
  pricesMap,
  activePositionId,
  onSetActivePositionId,
  savedPositions,
  savedPositionDrafts,
  onUpdateSavedPositionDraft,
  hasUnsavedChanges,
  onEditSavedPosition,
  onDeleteSavedPosition,
  isFetchingSavedPositions,
  setForceDefaultZoom,
}: SavedPositionsProps) => {
  const { allVolDataCall, allVolDataPut } = useOptionsCalculatorData();
  const { openToast } = useToast();
  const { getRiskFreeRate } = useOptionsCalculatorActions();
  const handleEditPosition = useCallback(
    async (
      positionId: PositionId,
      position: Partial<PositionType>,
      sym: string,
    ) => {
      try {
        await onEditSavedPosition(positionId, position, sym);
      } catch (error) {
        console.error('Failed to edit saved position:', error);
        openToast({
          message: 'Failed to edit saved position',
          type: 'error',
        });
      }
    },
    [onEditSavedPosition, openToast],
  );

  const handleDeletePosition = useCallback(
    async (positionId: PositionId) => {
      try {
        await onDeleteSavedPosition(positionId);
      } catch (error) {
        console.error('Failed to delete saved position:', error);
        openToast({
          message: 'Failed to delete saved position',
          type: 'error',
        });
      }
    },
    [onDeleteSavedPosition, openToast],
  );

  if (isFetchingSavedPositions) {
    return (
      <Stack
        spacing={3}
        alignItems="center"
        height="100%"
        justifyContent="center"
        sx={{ px: 3, py: 4 }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading saved positions...
        </Typography>
      </Stack>
    );
  }

  if (!savedPositions || savedPositions.length === 0) {
    return (
      <Stack
        spacing={3}
        alignItems="center"
        height="100%"
        justifyContent="center"
        sx={{ px: 3, py: 4 }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Saved Positions
        </Typography>
        <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
          No saved positions found. Save your first position to see it here.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ py: 2, height: '100%' }}>
      {savedPositions
        .sort((a, b) => {
          // Sort by created_at in descending order (latest first)
          const aCreatedAt = new Date(a.created_at || Date.now()).getTime();
          const bCreatedAt = new Date(b.created_at || Date.now()).getTime();
          return bCreatedAt - aCreatedAt;
        })
        .map((originalPosition: PositionType, index: number) => {
          // Get the draft version or fall back to original
          const draftPosition =
            savedPositionDrafts.get(originalPosition.id) || originalPosition;

          const sym = draftPosition.sym;

          const positionHasUnsavedChanges =
            hasUnsavedChanges.get(originalPosition.id) || false;

          const adjustedVolDataCall = getAdjustedVolData(
            allVolDataCall.get(sym) || [],
            draftPosition.iv_adjustments,
          );
          const adjustedVolDataPut = getAdjustedVolData(
            allVolDataPut.get(sym) || [],
            draftPosition.iv_adjustments,
          );

          const legIVs = new Map(
            draftPosition.option_legs
              .map((leg) => {
                const volData =
                  leg.option_type === OptionType.CALL
                    ? allVolDataCall.get(sym)
                    : allVolDataPut.get(sym);

                if (!volData || greeksLoading) {
                  return null;
                }

                const iv = getVolFromTableData(volData, leg.strike, leg.expiry);

                return [leg.leg_id, iv] as const;
              })
              .filter(
                (item): item is [number, number] =>
                  item != null && item[1] != null,
              ),
          );

          const adjustedLegIVs = new Map(
            draftPosition.option_legs
              .map((leg) => {
                const adjustedVolData =
                  leg.option_type === OptionType.CALL
                    ? adjustedVolDataCall
                    : adjustedVolDataPut;

                if (!adjustedVolData || greeksLoading) {
                  return null;
                }

                const iv = getVolFromTableData(
                  adjustedVolData,
                  leg.strike,
                  leg.expiry,
                );
                return [leg.leg_id, iv] as const;
              })
              .filter(
                (item): item is [number, number] =>
                  item != null && item[1] != null,
              ),
          );

          return (
            <Position
              key={originalPosition.id}
              getRiskFreeRate={getRiskFreeRate}
              legIVs={legIVs}
              adjustedLegIVs={adjustedLegIVs}
              position={draftPosition}
              originalPosition={originalPosition} // Pass original for comparison
              onSetPosition={(updatedPosition: Partial<PositionType>) => {
                onUpdateSavedPositionDraft(
                  originalPosition.id,
                  updatedPosition,
                );
              }}
              sym={sym}
              isActive={activePositionId === originalPosition.id}
              greeksLoading={greeksLoading}
              greeks={greeksForSyms?.get(sym)}
              greekStats={
                greekStatsForSyms?.get(sym)?.[GreeksStatsLookback.NINETY]
              }
              strikeDataPerExpiration={strikeDataPerExpiration}
              currentPrice={pricesMap?.get(sym)}
              onSetActivePositionId={onSetActivePositionId}
              editSavedPosition={(
                position: Partial<PositionType>,
                sym: string,
              ) => handleEditPosition(originalPosition.id, position, sym)}
              deleteSavedPosition={() =>
                handleDeletePosition(originalPosition.id)
              }
              hasUnsavedChanges={positionHasUnsavedChanges}
              isCollapsible
              defaultExpanded={index === 0}
              setForceDefaultZoom={setForceDefaultZoom}
            />
          );
        })}
    </Stack>
  );
};

export default SavedPositions;
